package com.siriusxm.pia.views.unifiedaggregator.relationships

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.RelationshipActor
import com.siriusxm.pia.rest.unifiedaggregator.RelationshipSchema
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.apiCall
import com.siriusxm.unifiedcontent.console.aggregator.allowsRelationship
import com.siriusxm.unifiedcontent.console.aggregator.relationshipCreateAffordance
import com.siriusxm.unifiedcontent.console.aggregator.rememberCapabilities
import com.siriusxm.unifiedcontent.console.aggregator.consoleProducer
import unifiedcontent.aggregator.BatchingRequest
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text

/**
 * View of all relationships schemas
 */
@Composable
fun ServiceViewContext.relationships(context: AggregatorService, initialSelect: String? = null) {
    var startedRebuild by remember { mutableStateOf(false) }
    val selected = context.relationships.find { it.id == initialSelect }
    fun select(schema: RelationshipSchema) {
        context.push("aggregator/relationships/${schema.id}")
    }

    serviceContentView({
        breadcrumbs {
            crumb("Aggregator", "aggregator")
            crumb("Relationships", "aggregator/relationships".takeIf { selected != null })
            if (selected != null) {
                crumb(selected.name, null)
            }
        }
    }) {
        splitSelectionView<RelationshipSchema> {
            title = "Relationships"
            items = context.relationships
            selection = selected
            onSelect {
                select(it)
            }
            renderListItem {
                it.name
            }

            render { schema ->
                // Offered when the console producer allows this type (relationship edits go out as
                // that producer and the API enforces its allowedRelationships, UCA-1151/UCA-1152) and
                // AVP allows CREATE with neither side chosen yet. There is no admin bypass on that
                // path. The editor checks the exact pair again before submitting.
                val producerAllows = schema.editable &&
                    consoleProducer(context.producers, context.context.configuration.clientId)
                        ?.allowsRelationship(schema.id) == true
                val createAllowed by context.context.rememberCapabilities(
                    listOfNotNull(relationshipCreateAffordance(schema.id).takeIf { producerAllows })
                )
                val canEditRelationship = producerAllows && createAllowed?.values?.firstOrNull() == true

                entityView({
                    title = schema.name

                    if (canEditRelationship) {
                        action("Create Relationship", true) {
                            // Encode the id first, then the assembled path once as a query value —
                            // see the same construction in EntityRelationships for why the order
                            // matters.
                            val returnPath = "relationships/${encodeURIComponent(schema.id)}"
                            context.navigate(
                                "relationships/edit?relationship=${
                                    encodeURIComponent(
                                        schema.id
                                    )
                                }&return=${encodeURIComponent(returnPath)}"
                            )
                        }
                    }
                    if (context.isAdmin()) {
                        action {
                            title = "Rebuild Relationship"
                            enabled = !startedRebuild
                            primary = false
                            showProgressOnAction = true

                            action {
                                context.apiCall("Rebuild failed for ${schema.name}") {
                                    context.clientApi.startBatchJob(
                                        BatchingRequest("Rebuild relationships", buildJsonObject {
                                            put("segmentTotal", 1000)
                                            put("schemaId", schema.id)
                                        })
                                    )
                                    startedRebuild = true
                                    context.context.info(
                                        "Rebuilding relationship started for ${schema.name}. This could take several minutes to complete."
                                    )
                                }
                            }
                        }
                    }
                }) {
                    box({
                        paddedContent = false
                    }) {
                        Div({
                            style {
                                backgroundColor(SXMUI.containerContentBackgroundColor.value())
                                padding(10.px)
                            }
                        }) {
                            Div({
                                style {
                                    fontSize(1.1.em)
                                    marginBottom(2.em)
                                    textAlign("center")
                                }
                            }) {
                                Text(schema.description)
                            }

                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    width(100.percent)
                                    textAlign("center")
                                }
                            }) {
                                @Composable
                                fun relationshipActor(actor: RelationshipActor) {
                                    Div({
                                        style {
                                            flex(1)
                                        }
                                    }) {
                                        val entityType = context.entityType(actor.type.name)
                                        H3 {
                                            Text(entityType?.name ?: actor.type.name)
                                        }
                                        actor.fieldLinkToSibling?.let {
                                            Div {
                                                Text("Linked field: ${it.removePrefix("$.")}")
                                            }
                                        }
                                    }
                                }

                                relationshipActor(schema.subject)
                                Div({
                                    style {
                                        width(100.px)
                                    }
                                }) {
                                    icon("compare_arrows") {
                                        size = IconSize.LARGE
                                    }
                                }
                                relationshipActor(schema.`object`)
                            }
                        }
                    }

                }
            }

            column {
                title = "Subject"
                content {
                    val subject = context.entityType(it.subjectType.name)
                    Text(subject?.name ?: it.subjectType.name)
                }
            }
            column {
                title = "Object"
                content {
                    val obj = context.entityType(it.objectType.name)
                    Text(obj?.name ?: it.objectType.name)
                }
            }
            column {
                title = "Editable"
                content {
                    if (it.editable) {
                        icon("check")
                    }
                }
            }
        }
    }
}
